import { useState, useRef } from "react";
import { useThemeContext } from "../../../../context/ThemeContext";
import {
  Upload,
  FileText,
  X,
  CheckCircle,
  AlertCircle,
  Download,
  FileCheck,
} from "lucide-react";

const Step3ExcelUpload = ({
  uploadedFile,
  onFileUpload,
  onBack,
  onNext,
  darkMode,
  existingFile = null, // Add this prop
  onDownloadExisting = () => {}, // Add this prop
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Red color theme
  const redTheme = {
    primary: "#DC2626", // Main red
    primaryLight: "#FEE2E2", // Light red for light mode
    primaryDark: "#7F1D1D", // Dark red for dark mode
    hover: "#B91C1C", // Hover state
    lightBg: "#FEF2F2", // Light background
    darkBg: "#1F1B1B", // Dark background
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (
      files.length > 0 &&
      (files[0].type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        files[0].type === "application/vnd.ms-excel")
    ) {
      onFileUpload(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files.length > 0) {
      onFileUpload(e.target.files[0]);
    }
  };

  const removeFile = () => {
    onFileUpload(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " bytes";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(2) + " KB";
    else return (bytes / 1048576).toFixed(2) + " MB";
  };

  return (
    <div
      className={`p-6 rounded-xl mb-4 transition-all duration-300 ${
        darkMode
          ? "bg-gray-900 border border-gray-800"
          : "bg-white border border-gray-200 shadow-sm"
      }`}
    >
      <div className="mb-6">
        <h3
          className={`text-xl font-bold mb-2 ${
            darkMode ? "text-white" : "text-gray-900"
          }`}
        >
          Upload Student Marks
        </h3>

        <p
          className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}
        >
          Upload your Excel file containing student marks. Ensure it follows the
          template structure for accurate processing.
        </p>
      </div>

      {/* Upload Area */}
      <div
        className={`rounded-lg mb-4 transition-all duration-300 ${
          isDragging
            ? `${darkMode ? "bg-red-900/30" : "bg-red-50"} border-2 border-dashed border-red-500`
            : `${darkMode ? "bg-gray-800 border-2 border-dashed border-gray-700" : "bg-gray-50 border-2 border-dashed border-gray-300"}`
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileSelect}
          className="hidden"
          id="excelUpload"
          ref={fileInputRef}
        />
        <label
          htmlFor="excelUpload"
          className={`flex flex-col items-center justify-center p-6 cursor-pointer transition-all duration-300 ${
            isDragging ? "scale-[1.02]" : ""
          }`}
        >
          <div
            className={`p-3 rounded-full mb-3 ${
              darkMode
                ? isDragging
                  ? "bg-red-800"
                  : "bg-gray-700"
                : isDragging
                  ? "bg-red-100"
                  : "bg-gray-200"
            }`}
          >
            <Upload
              size={20}
              className={
                darkMode
                  ? isDragging
                    ? "text-red-200"
                    : "text-gray-300"
                  : isDragging
                    ? "text-red-600"
                    : "text-gray-600"
              }
            />
          </div>
          <p
            className={`text-sm font-medium mb-1 ${
              darkMode
                ? isDragging
                  ? "text-red-200"
                  : "text-gray-300"
                : isDragging
                  ? "text-red-700"
                  : "text-gray-700"
            }`}
          >
            {isDragging ? "Drop your file here" : "Drag & Drop your file here"}
          </p>
          <p
            className={`text-xs ${
              darkMode
                ? isDragging
                  ? "text-red-300"
                  : "text-gray-500"
                : isDragging
                  ? "text-red-600"
                  : "text-gray-500"
            }`}
          >
            or click to browse • Excel files only (.xlsx, .xls)
          </p>
        </label>
      </div>

      {existingFile && (
        <div className="mb-4">
          <button
            onClick={onDownloadExisting}
            className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
              darkMode
                ? "bg-blue-700 hover:bg-blue-600 text-white"
                : "bg-blue-600 hover:bg-blue-700 text-white"
            }`}
          >
            <Download size={16} className="mr-2" />
            Download Existing Marks File
          </button>
          <p
            className={`text-xs mt-2 ${darkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            This file was previously uploaded for this course.
          </p>
        </div>
      )}
      {/* File Status */}
      {uploadedFile && uploadedFile.isExisting ? (
        <div
          className={`p-4 rounded-lg mb-4 border ${
            darkMode
              ? "bg-blue-900/20 border-blue-700/30"
              : "bg-blue-50 border-blue-200"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div
                className={`p-2 rounded-full mr-3 ${
                  darkMode ? "bg-blue-800/30" : "bg-blue-100"
                }`}
              >
                <FileCheck
                  size={18}
                  className={darkMode ? "text-blue-400" : "text-blue-600"}
                />
              </div>
              <div>
                <p
                  className={`text-sm font-medium ${
                    darkMode ? "text-blue-200" : "text-blue-800"
                  }`}
                >
                  {uploadedFile.name}
                </p>
                <p
                  className={`text-xs ${
                    darkMode ? "text-blue-400" : "text-blue-600"
                  }`}
                >
                  Existing file • Ready to process
                </p>
              </div>
            </div>
            <CheckCircle size={16} className="text-blue-500" />
          </div>
        </div>
      ) : (
        <div
          className={`p-4 rounded-lg mb-4 border ${
            darkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <div className="flex items-start">
            <div
              className={`p-2 rounded-full mr-3 ${
                darkMode ? "bg-gray-700" : "bg-gray-200"
              }`}
            >
              <AlertCircle
                size={18}
                className={darkMode ? "text-gray-500" : "text-gray-500"}
              />
            </div>
            <div>
              <p
                className={`text-sm font-medium mb-2 ${
                  darkMode ? "text-gray-300" : "text-gray-800"
                }`}
              >
                File requirements
              </p>
              <ul
                className={`text-xs space-y-1 ${
                  darkMode ? "text-gray-500" : "text-gray-600"
                }`}
              >
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500 mr-2"></div>
                  Excel format (.xlsx or .xls) only
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500 mr-2"></div>
                  Maximum file size: 10MB
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500 mr-2"></div>
                  Follow the provided template structure
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col-reverse md:flex-row justify-between items-center gap-3">
        <button
          className={`px-5 py-2.5 rounded-lg font-medium transition-colors w-full md:w-auto text-sm ${
            darkMode
              ? "bg-gray-800 text-white hover:bg-gray-700 border border-gray-700"
              : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
          }`}
          onClick={onBack}
        >
          Back
        </button>

        <button
          className={`px-5 py-2.5 rounded-lg font-medium flex items-center justify-center transition-colors w-full md:w-auto text-sm ${
            !uploadedFile
              ? `${
                  darkMode
                    ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                    : "bg-gray-200 text-gray-500 cursor-not-allowed"
                }`
              : `${
                  darkMode
                    ? "bg-red-700 hover:bg-red-600 text-white"
                    : "bg-red-600 hover:bg-red-700 text-white"
                }`
          }`}
          onClick={onNext}
          disabled={!uploadedFile}
        >
          {uploadedFile ? (
            <>
              <FileCheck size={16} className="mr-2" />
              Process Marks
            </>
          ) : (
            <>
              <AlertCircle size={16} className="mr-2" />
              Upload File to Continue
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default Step3ExcelUpload;
